import { Storage } from "@/services/Storage";
import axios from "axios";
import connectivityMonitor from '@/services/connectivityMonitor';

const normalizeString = (value) => String(value || '').toLowerCase();
const sortByDescription = (a, b) => String(a?.Description || '').localeCompare(String(b?.Description || ''));
const STOCK_SYNC_BATCH_SIZE = 500;
const STOCK_DELTA_PAGE_SIZE = 1000;
const DELTA_UNSUPPORTED_STATUS = new Set([400, 404, 405, 501]);
const HIGH_VOLUME_STOCK_THRESHOLD = 20000;
const MOBILE_FASTLINE_CAP = 2000;
const MOBILE_STOCK_CACHE_CAP = 5000;
const LARGE_SORT_THRESHOLD = 5000;
/**
 * How often the in-session sync is forced to take the full paginated path
 * even when a delta checkpoint exists. Mirrors Restaurant's `isFullSyncDue`
 * and exists for one reason: a delta-suppressed row (dirty skip, checkpoint
 * skew, backdated `updated_at`) would otherwise stay invisible until the
 * cashier logs out, which is the only other full-sync trigger.
 *
 * 24h balances correctness against server load (a full sync on a 20k SKU
 * catalogue runs 60-90s+). The Restaurant POS uses the same precedent.
 */
const FORCE_FULL_SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;
/**
 * Cap on how many "skipped" / "dropped" rows the integrity report keeps
 * as samples. Counts always reflect the full population; samples are
 * just enough to debug a recurring drop without flooding the EventBus
 * payload or the console.
 */
const INTEGRITY_REPORT_SKIP_SAMPLE_CAP = 50;
const INTEGRITY_REPORT_DROP_SAMPLE_CAP = 20;
/**
 * Page size for the synchronous first page of the paginated
 * `/stock/{DB}/page` endpoint. Sized to cover the FastLine grid
 * (capped at 2000) in two pages on desktop / four pages on mobile,
 * so the cashier sees fresh fast-line items within ~1.5s / ~2.5s.
 *
 * Server clamps anything > 1000 to 1000; we still send the smaller
 * mobile value because SUNMI-class Android devices parse smaller
 * payloads faster and avoid ANR.
 */
const STOCK_SYNC_FIRST_PAGE_SIZE_DESKTOP = 1000;
const STOCK_SYNC_FIRST_PAGE_SIZE_MOBILE = 500;
/**
 * Page size for the background pages that follow the synchronous
 * first page. The server still clamps at 1000; this constant is the
 * ceiling we ask for.
 */
const STOCK_SYNC_BACKGROUND_PAGE_SIZE = 1000;
/**
 * Per-page timeout (ms) for the paginated /stock/{DB}/page endpoint.
 * Bounds the worst case per-request so a single stuck page cannot hang
 * the entire Phase 2 loop. Phase 2 itself has no overall timeout —
 * it can take 60–90s for 20k SKUs on slow connections.
 */
const STOCK_FETCH_PAGE_TIMEOUT_MS = 30000;
/**
 * Safety cap for Phase 2 pagination: 1000 pages × 1000 items = 1M SKU ceiling.
 * Raised from 200 (200k) so large wholesale catalogues complete full sync
 * instead of stopping early. Still bounds infinite loops when the server
 * incorrectly keeps returning full pages forever.
 */
const MAX_PHASE_2_PAGES = 1000;
/**
 * Bounded concurrent HTTP fetches for Phase 2 full-sync pages (desktop / Electron).
 * Keeps server + client socket load modest while cutting wall time ~4× vs sequential.
 * Apply/persist stays single-writer regardless of this value.
 */
const STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP = 4;
/**
 * Lower Phase 2 fetch concurrency on native mobile (Capacitor / SUNMI-class).
 * Mobile radios and JS heaps handle fewer parallel large JSON payloads cleanly.
 */
const STOCK_SYNC_PAGE_CONCURRENCY_MOBILE = 2;
const toIsoString = (value) => String(value || '');
const isNativeMobileRuntime = () => {
    try {
        if (window?.androidMode) return true;
        return !!(window?.Capacitor &&
            typeof window.Capacitor.isNativePlatform === 'function' &&
            window.Capacitor.isNativePlatform());
    } catch (_) {
        return !!window?.androidMode;
    }
};
/**
 * Pick Phase 2 page-fetch concurrency for the current runtime.
 * Desktop/Electron uses the higher pool; native mobile uses the lower pool
 * so cold sync stays fast without saturating device network stacks.
 *
 * @returns {number} max in-flight `/stock/{DB}/page` requests during Phase 2
 */
const getStockSyncPageConcurrency = () => (
    isNativeMobileRuntime()
        ? STOCK_SYNC_PAGE_CONCURRENCY_MOBILE
        : STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP
);

/**
 * Module-level promise lock for stock syncs.
 *
 * Concurrent `getAllStock` calls (periodic timer from syncService.js
 * + manual refresh from addStockItem) would otherwise race on
 * `mergeServerStockItems` and double-write the same rows. The lock
 * serialises them so the second call awaits the first via
 * `withStockSyncLock`.
 */
let _stockSyncPromise = null;

/**
 * Wrap an async stock-sync function in the module-level lock so
 * concurrent callers execute serially. Errors from the in-flight
 * sync are swallowed by `.catch(() => undefined)` so a failed sync
 * does not poison the next caller; the next sync is independent.
 *
 * @template T
 * @param {() => Promise<T>} syncFn the sync function to run under the lock
 * @returns {Promise<T>} the result of the wrapped syncFn
 */
const withStockSyncLock = (syncFn) => {
    _stockSyncPromise = (_stockSyncPromise || Promise.resolve())
        .catch(() => undefined)
        .then(() => syncFn());
    return _stockSyncPromise;
};

/**
 * Empty stock-sync progress snapshot (Flutter SyncProgress parity when idle).
 * Loader treats active=false as hidden overlay for the stock path.
 *
 * @returns {{active: boolean, message: string, loaded: number, total: number, fraction: number|null}}
 */
const emptyStockSyncProgress = () => ({
    active: false,
    message: '',
    loaded: 0,
    total: 0,
    fraction: null,
    finished: false,
});

/**
 * Last published whole-percent for throttle (Flutter ~2% step). Reset on clear.
 * Prevents Loader thrashing on large catalogues while still advancing the bar.
 */
let _lastStockProgressPct = -1;

/**
 * Publish stock download progress to EventBus + Vuex.
 * Mirrors Flutter SyncProgress.stock / StoreSyncLoadingView: loaded/total/fraction
 * plus a human phase message. Determinate updates are throttled to ~2% steps
 * unless force=true (page 0, start, complete). Failures never break the sync.
 *
 * @param {Object|null} context Vuex action context (commit); optional for retries without context
 * @param {Object} opts
 * @param {number} [opts.loaded=0] cumulative rows applied so far
 * @param {number} [opts.total=0] server catalogue total when known
 * @param {string} [opts.message='Downloading stock…'] phase label
 * @param {boolean} [opts.active=true] whether the blocking overlay should show
 * @param {boolean} [opts.force=false] bypass 2% throttle
 * @param {boolean} [opts.showOverlay=true] false for background/periodic sync (no POS block)
 * @param {number} [opts.page] zero-based page index (EventBus consumers)
 * @param {boolean} [opts.hasMore] whether more pages are expected
 */
const publishStockProgress = (context, opts = {}) => {
    const loadedN = Math.max(0, Number(opts.loaded) || 0);
    let totalN = Math.max(0, Number(opts.total) || 0);
    const activeWanted = opts.active !== false;
    const showOverlay = opts.showOverlay !== false;
    const force = opts.force === true;
    // finished=true only when the whole sync (including local save) is done.
    // Until then never claim 100% — that was the "100% of 10,000" while still working bug.
    const finished = opts.finished === true;
    const hasMore = opts.hasMore === true;

    // While more pages may arrive, total must not sit below loaded (server undercount).
    if (!finished && (hasMore || loadedN > totalN) && loadedN > 0) {
        totalN = Math.max(totalN, loadedN);
    }

    let fraction = totalN > 0
        ? Math.min(1, Math.max(0, loadedN / totalN))
        : null;
    let pct = totalN > 0
        ? Math.min(100, Math.max(0, Math.floor((loadedN / totalN) * 100)))
        : -1;

    // Cap at 99% until finished so "Downloading/Saving" never shows 100%.
    if (!finished && pct >= 100) {
        pct = 99;
        fraction = 0.99;
    }
    if (!finished && fraction != null && fraction >= 1) {
        fraction = 0.99;
        pct = Math.min(pct < 0 ? 99 : pct, 99);
    }

    // Throttle mid-sync UI only when not forced. Every applied page uses force:true
    // so page steps (10%, 20%, …) always show.
    if (
        activeWanted
        && !force
        && pct >= 0
        && pct < 100
        && _lastStockProgressPct >= 0
        && (pct - _lastStockProgressPct) < 2
    ) {
        return;
    }
    if (pct >= 0) {
        _lastStockProgressPct = pct;
    }

    const payload = {
        active: !!(activeWanted && showOverlay),
        message: String(opts.message || 'Downloading stock…'),
        loaded: loadedN,
        total: totalN,
        fraction,
        finished,
        page: opts.page != null ? Number(opts.page) : undefined,
        hasMore,
    };

    try {
        if (window?.EventBus && typeof window.EventBus.$emit === 'function') {
            window.EventBus.$emit('stock:sync-progress', payload);
        }
    } catch (_) {
        // listener errors must not break the sync
    }

    try {
        if (context && typeof context.commit === 'function') {
            context.commit('setStockSyncProgress', payload);
        }
    } catch (_) {
        // commit failures must not break the sync
    }
};

/**
 * How long to hold the 100% / "Stock ready" frame so cashiers can see completion
 * before the overlay closes (avoids 99% → instant disappear).
 */
const STOCK_PROGRESS_COMPLETE_HOLD_MS = 400;

/**
 * Clear stock progress UI and fire stock:sync-complete (Flutter dialog pop parity).
 *
 * @param {Object|null} context Vuex action context
 * @param {'complete'|'partial'|string} result outcome label
 * @param {Object} [extra] fields merged onto the complete EventBus payload
 */
const clearStockProgress = (context, result = 'complete', extra = {}) => {
    _lastStockProgressPct = -1;
    const idle = emptyStockSyncProgress();
    try {
        if (window?.EventBus && typeof window.EventBus.$emit === 'function') {
            window.EventBus.$emit('stock:sync-complete', { result, ...extra });
        }
    } catch (_) {
        // listener errors must not break the sync
    }
    try {
        if (context && typeof context.commit === 'function') {
            context.commit('setStockSyncProgress', idle);
        }
    } catch (_) {
        // commit failures must not break the sync
    }
};

/**
 * Publish true 100% (finished) briefly, then clear the overlay.
 * Mid-download still caps at 99%; only this path is allowed to show 100%.
 *
 * @param {Object|null} context Vuex action context
 * @param {'complete'|'partial'|string} result outcome label
 * @param {{totalSynced?: number, lastFullCount?: number, showOverlay?: boolean}} [payload]
 * @returns {Promise<void>}
 */
const finishStockProgress = async (context, result = 'complete', payload = {}) => {
    const showOverlay = payload.showOverlay !== false;
    if (showOverlay) {
        const totalSynced = Math.max(0, Number(payload.totalSynced) || 0);
        const lastFull = Math.max(0, Number(payload.lastFullCount) || 0);
        const total = Math.max(totalSynced, lastFull, 1);
        const loaded = Math.max(totalSynced, total);
        publishStockProgress(context, {
            loaded,
            total,
            message: result === 'partial' ? 'Stock partially loaded' : 'Stock ready',
            force: true,
            finished: true,
            active: true,
            showOverlay: true,
            hasMore: false,
        });
        await new Promise((resolve) => setTimeout(resolve, STOCK_PROGRESS_COMPLETE_HOLD_MS));
    }
    clearStockProgress(context, result, payload);
};

/**
 * @deprecated use publishStockProgress — kept name at call sites via alias below
 * @param {Object|null} context
 * @param {Object} payload
 */
const emitProgress = (context, payload) => {
    if (payload && typeof payload === 'object' && payload.loaded == null && payload.page != null) {
        // Legacy page/total shape from older call sites: approximate loaded from page index.
        const page = Math.max(0, Number(payload.page) || 0);
        const total = Math.max(0, Number(payload.total) || 0);
        const pageSize = STOCK_SYNC_BACKGROUND_PAGE_SIZE;
        const loadedApprox = total > 0
            ? Math.min(total, (page + 1) * pageSize)
            : (page + 1) * pageSize;
        publishStockProgress(context, {
            loaded: loadedApprox,
            total,
            hasMore: !!payload.hasMore,
            page,
            message: 'Downloading stock…',
            showOverlay: payload.showOverlay !== false,
            force: payload.force === true,
        });
        return;
    }
    publishStockProgress(context, payload || {});
};

/**
 * Fire complete + 100% flash + clear overlay when Phase 2 finishes.
 *
 * @param {Object|null} context Vuex action context
 * @param {'complete'|'partial'} result outcome of the Phase 2 loop
 * @param {{totalSynced: number, lastPageNum: number, lastFullCount: number, showOverlay?: boolean}} payload completion payload
 * @returns {Promise<void>}
 */
const emitComplete = async (context, result, payload) => {
    await finishStockProgress(context, result, payload || {});
};

/**
 * Rebuild Vuex FastLine grid from SQL (same path as the Fast Line button).
 *
 * Why: after paginated apply, in-memory `_stockCache` can still hold an
 * older page-0 snapshot; getAllStock would rebuild the grid from that.
 * getFastLine always reflects `stock_items.fast_line = 1` after cache drop.
 *
 * @param {Object} context Vuex action context
 * @param {{cap?: boolean}} [opts] when cap true, apply MOBILE_FASTLINE_CAP
 * @returns {Promise<void>}
 */
const refreshFastLineGrid = async (context, opts = {}) => {
    await Storage.invalidateStockCache();
    let fastLine = await Storage.getFastLine();
    fastLine = Array.isArray(fastLine) ? fastLine : [];
    if (opts.cap && fastLine.length > MOBILE_FASTLINE_CAP) {
        fastLine = fastLine.slice(0, MOBILE_FASTLINE_CAP);
    }
    await context.commit('stockByPLU', { items: fastLine, persist: false });
};

const chunkArray = (items, size) => {
    const source = Array.isArray(items) ? items : [];
    const chunkSize = Math.max(1, Number(size) || STOCK_SYNC_BATCH_SIZE);
    const chunks = [];
    for (let i = 0; i < source.length; i += chunkSize) {
        chunks.push(source.slice(i, i + chunkSize));
    }
    return chunks;
};

/**
 * Coerce a "truthy-flag" value to strict boolean. The server historically
 * sent `archived` as `false`, `true`, `0`, `1`, or omitted the field entirely
 * depending on the row's age and the store's onboarding path. The grid's
 * `item.archived === false` filter is strict, so any non-`false` shape hides
 * the item even though the SQL `archived = 0` path considers it active.
 *
 * Tolerant on purpose: missing / `null` / `undefined` / `0` / `"0"` all map
 * to `false` so the grid shows the item rather than silently dropping it.
 *
 * @param {*} value raw server value for a boolean flag
 * @returns {boolean}
 */
const toBoolFlag = (value) => value === true || value === 1 || value === '1' || value === 'true';

/**
 * Single normalization boundary for the two flags the grid and view filters
 * read directly: `archived` (strict boolean) and `FastLine` (number 0|1).
 *
 * Why a single boundary: every code path that puts an item on
 * `state.stock` / `state.stockProducts` / `state.stockCache` MUST produce
 * these shapes consistently, because downstream readers compare with
 * `=== false` and `=== 1`. Without this, a payload with `archived: 0`
 * survives the round trip through `JSON.stringify` and defeats the filter.
 *
 * @param {Object|null|undefined} item stock row (or null/undefined)
 * @returns {Object|null|undefined} same item with flags coerced; null/undefined passes through
 */
const normalizeStockFlags = (item) => {
    if (!item || typeof item !== 'object') return item;
    const fastLineRaw = item.FastLine ?? item.fast_line;
    return {
        ...item,
        archived: toBoolFlag(item.archived),
        FastLine: (fastLineRaw === true || fastLineRaw === 1 || fastLineRaw === '1') ? 1 : 0,
    };
};

/**
 * Decide whether the next sync must take the paginated full path rather
 * than the (cheaper but suppressive) delta path. Mirrors Restaurant POS.
 *
 * Forced when:
 *   - delta has been marked unsupported (server returned 400/404/405/501),
 *     in which case every sync must be a full one until the server recovers;
 *   - no `lastFullSync` recorded yet (first run after this code lands, or
 *     after logout cleared the sync state);
 *   - the elapsed time since `lastFullSync` exceeds the 24h interval.
 *
 * @param {Object} syncState value from Storage.getStockSyncState()
 * @param {number} nowMs current epoch ms (parameter for testability)
 * @returns {boolean}
 */
const isFullSyncDue = (syncState, nowMs) => {
    if (syncState?.deltaUnsupported === true) return true;
    const lastFullSync = syncState?.lastFullSync;
    if (!lastFullSync) return true;
    const lastMs = new Date(lastFullSync).getTime();
    if (!Number.isFinite(lastMs)) return true;
    return (nowMs - lastMs) >= FORCE_FULL_SYNC_INTERVAL_MS;
};

/**
 * Module-level accumulator for one sync run's integrity telemetry. Lives
 * outside Vuex because the report is per-run metadata, not domain state;
 * no component reads it until the run completes and the EventBus fires.
 *
 * Counts always reflect the full population; `skipped[]` / `noPluDropSamples[]`
 * are bounded samples for debugging without flooding payloads.
 *
 * @returns {Object} fresh stats object
 */
const createSyncStats = () => ({
    source: null,
    serverTotal: null,
    appliedCount: 0,
    skipped: [],
    skippedCount: 0,
    noPluDrops: 0,
    noPluDropSamples: [],
    duplicatePlu: 0,
    capped: null,
    outcome: null,
    startedAt: null,
    finishedAt: null,
});

/**
 * Module-scope singleton: serialised by `withStockSyncLock` so concurrent
 * `getAllStock` / `syncStockDelta` calls never corrupt each other's stats.
 */
let _currentSyncStats = null;

const getSyncStats = () => {
    if (!_currentSyncStats) _currentSyncStats = createSyncStats();
    return _currentSyncStats;
};

/**
 * Begin a fresh run. Called at the top of `doGetAllStock` and at the top
 * of `syncStockDelta`. The lock guarantees no interleaving.
 *
 * @param {('full'|'delta')} source identifies which sync path produced this run
 */
const resetSyncStats = (source) => {
    _currentSyncStats = createSyncStats();
    _currentSyncStats.source = source;
    _currentSyncStats.startedAt = new Date().toISOString();
};

/**
 * Record a row dropped before persistence because of a missing/empty PLU.
 * Bounded sample list for debugging.
 *
 * @param {Object} item the dropped server row (raw)
 * @param {string} reason short tag describing the drop shape
 */
const recordRowDrop = (item, reason) => {
    const stats = getSyncStats();
    stats.noPluDrops += 1;
    if (stats.noPluDropSamples.length < INTEGRITY_REPORT_DROP_SAMPLE_CAP) {
        stats.noPluDropSamples.push({
            id: item?.id,
            description: item?.Description || item?.description || null,
            reason: reason || 'no-plu',
        });
    }
};

/**
 * Record a delta-reconciliation skip. Called from `syncStockDelta` once
 * per row that the server returned but `reconcileServerStockUpdates` held
 * back (dirty qty, server-older-than-local, etc.).
 *
 * @param {string} plu the skipped row's PLU
 * @param {string} reason short tag from the reconciliation branch
 */
const recordSkip = (plu, reason) => {
    const stats = getSyncStats();
    stats.skippedCount += 1;
    if (stats.skipped.length < INTEGRITY_REPORT_SKIP_SAMPLE_CAP) {
        stats.skipped.push({ plu, reason });
    }
};

const recordApply = (count) => {
    if (!Number.isFinite(count) || count <= 0) return;
    getSyncStats().appliedCount += count;
};

/**
 * Record a fastline-map collision (first-wins dedup in the mutation). The
 * first item for a given PLU always wins; the second item is silently
 * discarded. Surfacing the count lets us spot a backend data issue where
 * two rows share a PLU.
 */
const recordDuplicatePlu = () => {
    getSyncStats().duplicatePlu += 1;
};

/**
 * Record that the visible grid was truncated to a memory cap. Critical for
 * mobile debugging because cap truncation is by-design but its magnitude
 * matters — a 19,999-item drop on a 20k catalogue means the cashier only
 * sees 1 of 20000 fastline items.
 *
 * @param {number} cap the cap value that was applied (e.g. MOBILE_FASTLINE_CAP)
 * @param {number} dropped count of rows removed by the cap
 */
const recordCap = (cap, dropped) => {
    getSyncStats().capped = { cap, dropped };
};

/**
 * Close out the current run. Returns a fresh object the caller can spread
 * into the EventBus payload (so listeners never observe live mutation of
 * the module singleton).
 *
 * @param {string} outcome 'complete' | 'partial' | 'failed' | 'no-change'
 * @param {Object} [extra] extra fields to fold into the final report
 * @returns {Object} finalized report
 */
const finalizeSyncStats = (outcome, extra) => {
    const stats = getSyncStats();
    stats.outcome = outcome;
    stats.finishedAt = new Date().toISOString();
    return Object.assign({}, stats, extra || {});
};

/**
 * Emit one `console.warn` per finished sync run, carrying the integrity
 * report. Never logs per-row (would flood on big catalogues) and never
 * logs during delta runs that are no-ops — only the consolidated summary.
 *
 * @param {Object} report finalized stats from finalizeSyncStats
 */
const emitIntegritySummary = (report) => {
    if (!report) return;
    // Suppress the noise on healthy no-op deltas (zero applied, zero skipped,
    // zero drops). Still attach to the EventBus for callers that care.
    const quiet = report.source === 'delta'
        && report.outcome === 'no-change'
        && report.appliedCount === 0
        && report.skippedCount === 0
        && report.noPluDrops === 0
        && report.duplicatePlu === 0
        && !report.capped;
    if (!quiet) {
        // eslint-disable-next-line no-console
        console.warn('[Stock Integrity]', report);
    }
};

/**
 * Normalize one stock row from OnlineSalesServer into the shape we persist
 * locally (see stock_items.payload in src/services/StockRepository.js).
 *
 * Server-only fields are stripped so payloads stay lean and each concern
 * stays with the subsystem that owns it:
 *   - CreatedDate, args, where_between → legacy server diagnostics, unused by POS
 *   - Image                            → image bytes are fetched lazily from
 *                                         stockImageApiBase on demand; never
 *                                         part of the stock sync payload
 *   - Price1..Price5                   → SellingIncl is the price POS uses
 *   - StockSetting                     → extracted by normalizeStockSetting and
 *                                         stored separately in `stockSetting`
 *
 * @param {Object} item raw row from /stock/{DB} or /stock/{DB}/delta
 * @returns {Object|null} the cleaned item, or null when PLU is missing
 */
const normalizeServerStockItem = (item) => {
    if (!item || item.PLU === undefined || item.PLU === null) {
        // Track silent drops so the integrity report can prove the API
        // returned a row that was unusable. Without this, a malformed PLU
        // vanished without any trace.
        recordRowDrop(item, 'no-plu');
        return null;
    }
    const normalized = { ...item };
    delete normalized.CreatedDate;
    delete normalized.Image; // Keep image loading lazy/background (not in stock sync path)
    delete normalized.Price1;
    delete normalized.Price2;
    delete normalized.Price3;
    delete normalized.Price4;
    delete normalized.Price5;
    delete normalized.args;
    delete normalized.where_between;
    delete normalized.StockSetting;
    // Single normalization boundary (Plan §A2): every server row exits this
    // function with `archived` as strict boolean and `FastLine` as 0|1 so
    // the grid's `=== false` / `=== 1` checks are un-defeatable downstream.
    return normalizeStockFlags(normalized);
};

/**
 * Extract the per-item `StockSetting` flags nested inside a server stock row
 * into a flat object keyed by `plu` (lowercase).
 *
 * The POS UI reads these flags directly: cookingLink (kitchen routing),
 * askForPrice/askForDescription/askForBoth (prompts at scan time), scale,
 * NonStock (qty not tracked), tourism (tax override), password_protected,
 * printer (per-item slip), getQtyFromPrice (qty derived from price).
 *
 * Items without a `StockSetting` still return a record with safe defaults
 * so consumers (sale line, cooking instruction, search bar) never crash on
 * undefined. Returns null when PLU is missing (the row cannot be keyed).
 *
 * @param {Object} item raw row from OnlineSalesServer (must include PLU and optionally StockSetting)
 * @returns {Object|null} flat setting record keyed by lower(PLU), or null when PLU is missing
 */
const normalizeStockSetting = (item) => {
    if (!item || item.PLU === undefined || item.PLU === null) return null;
    const set = item.StockSetting;
    if (set) {
        return {
            plu: item.PLU,
            cookingLink: set.CookingLink,
            mtv: set.mtv,
            printer: set.printer,
            askForPrice: set.askForPrice,
            askForDescription: set.askForDescription,
            askForBoth: set.askForBoth,
            NonStock: set.NonStock,
            tourism: set.tourism,
            getQtyFromPrice: set.getQtyFromPrice,
            password_protected: set.password_protected,
            scale: set.Scale || set.scale
        };
    }
    return {
        plu: item.PLU,
        mtv: 0.00,
        tourism: false,
        NonStock: false,
        cookingLink: false,
        askForPrice: false,
        askForDescription: false,
        askForBoth: false,
    };
};

const extractUpdatedAt = (item) => {
    if (!item) return '';
    return toIsoString(item.updated_at || item.UpdatedAt || item.updatedAt || item.LastUpdated || '');
};

const getMaxUpdatedAt = (items, fallbackIso = '') => {
    let maxIso = toIsoString(fallbackIso || '');
    const source = Array.isArray(items) ? items : [];
    for (const item of source) {
        const iso = extractUpdatedAt(item);
        if (iso && (!maxIso || iso > maxIso)) {
            maxIso = iso;
        }
    }
    return maxIso;
};

/**
 * Normalize the response body of a stock page (delta or full) into
 * `{ items, hasMore }` so the caller can drive a paging loop uniformly.
 *
 * Tolerates three wire shapes because the server has shipped each of these
 * over time:
 *   1. Bare JSON array (legacy)              → hasMore = items.length >= pageSize
 *   2. Envelope with `items` array           → standard new shape
 *   3. Envelope with `data` or `stock` array → older envelopes
 *
 * `hasMore` is also derived from `hasMore`, `more`, `nextOffset`, or
 * `total` when present, so callers can stop paging without an extra
 * round-trip.
 *
 * @param {*} payload response body from the server
 * @param {number} pageSize the page size used in the request (fallback hasMore signal)
 * @returns {{items: Array, hasMore: boolean}}
 */
const parseDeltaResponse = (payload, pageSize) => {
    if (Array.isArray(payload)) {
        return {
            items: payload,
            hasMore: payload.length >= pageSize
        };
    }

    const items =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.data) && payload.data) ||
        (Array.isArray(payload?.stock) && payload.stock) ||
        [];

    const hasMore = payload?.hasMore === true ||
        payload?.more === true ||
        (Number.isFinite(Number(payload?.nextOffset)) && Number(payload.nextOffset) >= 0) ||
        (Number.isFinite(Number(payload?.total)) && (Number(payload.total) > items.length));

    return { items, hasMore };
};

/**
 * Fetch one page from the new paginated stock endpoint:
 *   `GET ${serverApi}stock/${store.StoreDB}/page?pc=&user=&page=&size=`
 *
 * Returns `{ items, page, size, total, hasMore }` so the caller can
 * drive a paging loop and emit `stock:sync-progress` events. Reuses
 * `parseDeltaResponse` so all three legacy wire shapes (bare array,
 * `{items}`, `{data}` / `{stock}`) are still handled — the new
 * endpoint returns the `{items, hasMore, total}` envelope.
 *
 * Throws on transport / HTTP errors so the caller can fall back to
 * cache-only or trigger a retry; the response shape matches
 * `getAllStock`'s legacy error contract.
 *
 * @param {{StoreDB: string}} store the default store object (must have a non-empty StoreDB)
 * @param {{PC?: string}} [device] the device settings (PC code is sent for audit)
 * @param {{Username?: string}} [user] the current user (Username is sent for audit)
 * @param {{page: number, size: number}} options zero-based page index and page size
 * @returns {Promise<{items: Array, page: number, size: number, total: number, hasMore: boolean}>}
 */
const fetchStockPage = async (store, device, user, { page, size } = {}) => {
    if (!store || !store.StoreDB) {
        throw new Error('[Stock Module] fetchStockPage called without a default store (no StoreDB)');
    }
    const safePage = Math.max(0, Number(page) || 0);
    const safeSize = Math.max(1, Number(size) || STOCK_SYNC_BACKGROUND_PAGE_SIZE);
    const url = `${serverApi}stock/${store.StoreDB}/page`;
    const response = await axios.get(url, {
        params: {
            pc: device?.PC,
            user: user ? user.Username : undefined,
            page: safePage,
            size: safeSize,
        },
        timeout: STOCK_FETCH_PAGE_TIMEOUT_MS,
    });
    const payload = response?.data;
    const parsed = parseDeltaResponse(payload, safeSize);

    // Server may also surface hasMore on the envelope; prefer that signal.
    const envelopeHasMore = !!(payload && typeof payload === 'object' && (
        payload.hasMore === true ||
        payload.more === true ||
        (Number.isFinite(Number(payload.nextOffset)) && Number(payload.nextOffset) >= 0)
    ));

    const total = Number(payload?.total);
    return {
        items: Array.isArray(parsed.items) ? parsed.items : [],
        page: safePage,
        size: safeSize,
        total: Number.isFinite(total) && total >= 0 ? total : (Array.isArray(parsed.items) ? parsed.items.length : 0),
        hasMore: envelopeHasMore || parsed.hasMore === true,
    };
};

/**
 * Phase 2 of the paginated full sync: fetch pages 1..N with a bounded
 * concurrent worker pool, then apply each page in page-number order
 * (single-writer). Runs under the module-level lock (caller holds
 * `withStockSyncLock`) so concurrent getAllStock cannot race merges.
 *
 * Why parallel fetch + sequential apply:
 * - HTTP is I/O-bound; N concurrent `/page` calls cut wall time ~N× on large catalogues.
 * - `Storage.applyPaginatedStockPage` + Vuex merge must stay single-writer to avoid
 *   double-write corruption on the same PLU rows (same invariant as withStockSyncLock).
 *
 * Stop rules (unchanged from sequential Phase 2):
 * - empty page, short page (items.length < pageSize), or MAX_PHASE_2_PAGES safety cap.
 * - Mid-loop fetch error: keep already-applied pages, mark partial, stop.
 * - When a short/empty page is found, do not schedule pages beyond it; in-flight
 *   higher pages may finish but are discarded.
 *
 * @param {Object} args
 * @param {Object} args.context       Vuex action context for commits
 * @param {Object} args.store         default store (from Storage.getDefaultStore)
 * @param {Object} args.device        device settings (PC)
 * @param {Object} args.user          current user
 * @param {Object} args.firstPage     result of the synchronous page-0 fetch
 * @param {Object} args.syncState     current syncState from Storage
 * @param {boolean} args.deltaUnsupported  whether the delta endpoint was marked unsupported
 * @param {boolean} args.isNativeMobileRuntime  whether running on native mobile
 * @param {boolean} args.highVolumeMobileHint   whether high-volume mobile path is active
 * @param {boolean} [args.showOverlay=true]     false for background/periodic sync (no blocking bar)
 * @returns {Promise<{lastStockSync: string, totalSynced: number}>}
 */
const runBackgroundStockPages = async ({
    context,
    store,
    device,
    user,
    firstPage,
    syncState,
    deltaUnsupported,
    isNativeMobileRuntime,
    highVolumeMobileHint,
    showOverlay = true,
}) => {
    // Don't trust the server's hasMore — it can be wrong (e.g., always false on
    // page=0 of a 20k-item store). Use items.length < pageSize as the stop
    // signal: if a page returns fewer items than requested, we're done.
    // PAGE SIZES MUST MATCH ACROSS PHASE 0 AND PHASE 2. The server offsets
    // each page by `page * (requested size)` — so if page 0 was requested
    // with size 500 (mobile) and Phase 2 then asks for size 1000, the
    // server skips the band covered by the smaller window (offsets
    // 500..999 in the Lusaka 3,753-row catalogue, which was exactly where
    // Urea hid). Prefer the size the server echoes in the page-0 envelope
    // (`firstPage.size`) since that's the size page 0 actually requested.
    // Fall back to the default when the server doesn't echo one back.
    const echoedSize = Number(firstPage?.size);
    const pageSize = Number.isFinite(echoedSize) && echoedSize > 0
        ? echoedSize
        : STOCK_SYNC_BACKGROUND_PAGE_SIZE;
    // Prefer the boolean the caller already computed so we do not re-probe Capacitor
    // mid-sync; fall back to runtime helper if a caller omits the flag.
    const concurrency = Math.max(
        1,
        Number(
            isNativeMobileRuntime === true
                ? STOCK_SYNC_PAGE_CONCURRENCY_MOBILE
                : (isNativeMobileRuntime === false
                    ? STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP
                    : getStockSyncPageConcurrency())
        ) || STOCK_SYNC_PAGE_CONCURRENCY_DESKTOP
    );

    let maxUpdatedAt = getMaxUpdatedAt(
        Array.isArray(firstPage?.items) ? firstPage.items : [],
        new Date().toISOString()
    );
    let totalSynced = Array.isArray(firstPage?.items) ? firstPage.items.length : 0;
    let phase2Result = 'complete';
    let lastAppliedPage = 0;

    // Pre-compute exclusive upper bound from page-0 total when present.
    // pagesNeeded includes page 0; Phase 2 schedules page indices 1..pagesNeeded-1.
    // Cap at MAX_PHASE_2_PAGES so a bogus huge total cannot unbounded-schedule.
    // Live total may grow if a later page reports a higher catalogue total (Flutter parity).
    let maxPageExclusive = MAX_PHASE_2_PAGES;
    let liveCatalogueTotal = Number(firstPage?.total);
    if (!Number.isFinite(liveCatalogueTotal) || liveCatalogueTotal < 0) {
        liveCatalogueTotal = 0;
    }
    const recomputeMaxPageExclusive = () => {
        if (liveCatalogueTotal > 0) {
            const pagesNeeded = Math.ceil(liveCatalogueTotal / pageSize);
            maxPageExclusive = Math.min(Math.max(pagesNeeded, 1), MAX_PHASE_2_PAGES);
        }
    };
    recomputeMaxPageExclusive();
    if (!(Number.isFinite(Number(firstPage?.total)) && Number(firstPage.total) >= 0)) {
        // no page-0 total — keep safety cap only
        maxPageExclusive = MAX_PHASE_2_PAGES;
    }

    let nextPageToStart = 1;
    let stopNewFetches = false;
    let applyFinished = false;
    /** @type {Map<number, Promise<{pageNum: number, page: object|null, error: Error|null}>>} */
    const inflight = new Map();
    /** @type {Map<number, {page: object|null, error: Error|null}>} */
    const readyByPage = new Map();
    let nextPageToApply = 1;

    /**
     * Start one background page fetch and track it in the inflight map.
     * Yields once before the HTTP call so the cashier UI can process input.
     *
     * @param {number} pageNum zero-based page index to fetch
     * @returns {Promise<{pageNum: number, page: object|null, error: Error|null}>}
     */
    const startPageFetch = (pageNum) => {
        const task = (async () => {
            try {
                // Yield so barcode scans / FastLine taps stay responsive during Phase 2.
                await new Promise((resolve) => setTimeout(resolve, 0));
                const page = await fetchStockPage(store, device, user, {
                    page: pageNum,
                    size: pageSize,
                });
                return { pageNum, page, error: null };
            } catch (err) {
                return { pageNum, page: null, error: err instanceof Error ? err : new Error(String(err)) };
            }
        })();
        inflight.set(pageNum, task);
        return task;
    };

    /**
     * Fill the fetch pool up to `concurrency` while scheduling is still open.
     * Does not await — callers race/join inflight promises separately.
     */
    const fillFetchPool = () => {
        while (
            !stopNewFetches &&
            !applyFinished &&
            inflight.size < concurrency &&
            nextPageToStart < maxPageExclusive
        ) {
            const pageNum = nextPageToStart;
            nextPageToStart += 1;
            startPageFetch(pageNum);
        }
    };

    fillFetchPool();

    // Pump: wait for fetches, apply in strict page order, refill pool.
    while (!applyFinished) {
        if (!readyByPage.has(nextPageToApply)) {
            if (inflight.size === 0) {
                // Nothing left to wait for and next apply page never arrived — end cleanly.
                break;
            }
            // Wait for any in-flight page; park the result or discard if past stop.
            // After a short/empty/error page we keep only results still needed for
            // ordered apply (page === nextPageToApply); higher pages are dropped.
            const settled = await Promise.race(inflight.values());
            inflight.delete(settled.pageNum);
            const pastStop = applyFinished || (stopNewFetches && settled.pageNum > nextPageToApply);
            if (!pastStop) {
                readyByPage.set(settled.pageNum, { page: settled.page, error: settled.error });
            }
            fillFetchPool();
            continue;
        }

        const entry = readyByPage.get(nextPageToApply);
        readyByPage.delete(nextPageToApply);

        if (entry.error) {
            // Keep already-applied pages; mark partial; stop (same policy as sequential Phase 2).
            console.warn(
                `[Stock Module] Background stock page ${nextPageToApply} failed:`,
                entry.error?.message || entry.error
            );
            phase2Result = 'partial';
            applyFinished = true;
            stopNewFetches = true;
            readyByPage.clear();
            break;
        }

        const items = Array.isArray(entry.page?.items) ? entry.page.items : [];
        if (items.length === 0) {
            // Server returned no items — stop; discard any higher buffered pages.
            phase2Result = 'partial';
            applyFinished = true;
            stopNewFetches = true;
            readyByPage.clear();
            break;
        }

        // Single-writer apply path — never parallelise persist/merge.
        const normalized = items.map(normalizeServerStockItem).filter(Boolean);
        const settings = items.map(normalizeStockSetting).filter(Boolean);
        let pagePersistFailed = false;
        const applyAndRecord = async () => {
            const applyResult = await Storage.applyPaginatedStockPage(normalized);
            if (applyResult?.applied?.length) {
                context.commit('mergeServerStockItems', applyResult.applied);
            }
            // Plan §B3: every Phase-2 page contributes to the integrity report.
            // applyResult.applied is post-reconciliation (already filtered for
            // dirty-skip), so recordApply is the count that actually landed.
            recordApply(applyResult?.applied?.length || 0);
            if (Array.isArray(applyResult?.skipped)) {
                for (const skipped of applyResult.skipped) {
                    recordSkip(skipped?.plu, skipped?.reason || 'unknown');
                }
            }
        };
        try {
            await applyAndRecord();
        } catch (persistErr) {
            // Persist failure is silently swallowed further down — the page's
            // rows were never applied, but the loop carries on and bookkeeping
            // still records "complete". That is exactly how the device ends up
            // missing one full window (e.g. server `page=1`, the regression that
            // hid Urea on this till). Retry the page once after a brief backoff
            // (upserts are idempotent — SQL/vued commits repeat cleanly). If it
            // still fails, stop the pipeline like a fetch error does: a partial
            // run is cheaper than a permanently hole-y catalogue.
            console.warn(
                `[Stock Module] Background stock page ${nextPageToApply} persist failed, retrying once:`,
                persistErr?.message || persistErr
            );
            await new Promise((resolve) => setTimeout(resolve, 250));
            try {
                await applyAndRecord();
            } catch (retryErr) {
                console.warn(
                    `[Stock Module] Background stock page ${nextPageToApply} persist failed twice, stopping pipeline:`,
                    retryErr?.message || retryErr
                );
                phase2Result = 'partial';
                applyFinished = true;
                stopNewFetches = true;
                readyByPage.clear();
                pagePersistFailed = true;
            }
        }
        if (pagePersistFailed) {
            break;
        }
        if (settings.length > 0) {
            context.commit('mergeStockSettings', settings);
        }

        maxUpdatedAt = getMaxUpdatedAt(items, maxUpdatedAt);
        totalSynced += items.length;
        lastAppliedPage = nextPageToApply;

        // Grow live total if this page reports a higher catalogue count.
        const pageTotal = Number(entry.page?.total);
        if (Number.isFinite(pageTotal) && pageTotal > liveCatalogueTotal) {
            liveCatalogueTotal = pageTotal;
            recomputeMaxPageExclusive();
        }

        const stillHasMore = items.length >= pageSize
            && (nextPageToApply + 1) < maxPageExclusive;
        const displayTotal = liveCatalogueTotal > 0
            ? Math.max(liveCatalogueTotal, totalSynced)
            : totalSynced;

        // force:true so each page step (10%, 20%, …) always reaches the Loader.
        publishStockProgress(context, {
            loaded: totalSynced,
            total: displayTotal,
            page: nextPageToApply,
            hasMore: stillHasMore,
            message: 'Downloading stock…',
            showOverlay,
            force: true,
            finished: false,
        });

        if (items.length < pageSize) {
            // Short page — catalogue end. Stop scheduling; discard higher pages in-flight/buffer.
            stopNewFetches = true;
            applyFinished = true;
            readyByPage.clear();
            break;
        }

        nextPageToApply += 1;
        if (nextPageToApply >= maxPageExclusive) {
            applyFinished = true;
            break;
        }
        fillFetchPool();
    }

    // Drain leftover in-flight HTTP so we do not leave orphan requests after stop.
    if (inflight.size > 0) {
        await Promise.allSettled([...inflight.values()]);
        inflight.clear();
    }

    if (lastAppliedPage + 1 >= MAX_PHASE_2_PAGES || nextPageToStart >= MAX_PHASE_2_PAGES) {
        console.warn(`[Stock Module] Phase 2 hit safety cap (${MAX_PHASE_2_PAGES} pages); stopping`);
    }

    // Pages are applied — local reload/persist can still take seconds. Be honest:
    // "Saving stock…" at ≤99%, never "Downloading… 100%".
    const finalDisplayTotal = liveCatalogueTotal > 0
        ? Math.max(liveCatalogueTotal, totalSynced)
        : totalSynced;
    if (showOverlay) {
        publishStockProgress(context, {
            loaded: totalSynced,
            total: finalDisplayTotal,
            message: 'Saving stock…',
            showOverlay: true,
            force: true,
            finished: false,
            hasMore: false,
        });
    }

    // Drop caches and rebuild FastLine grid from SQL so the POS tiles match
    // the Fast Line button without requiring a manual click (page-N upserts).
    await refreshFastLineGrid(context, {
        cap: !!(isNativeMobileRuntime && highVolumeMobileHint),
    });

    // Persist sync state so the next getAllStock can take the delta
    // path (syncStockDelta) when it runs.
    await Storage.setStockSyncState({
        ...syncState,
        lastStockSync: maxUpdatedAt || new Date().toISOString(),
        lastFullSync: new Date().toISOString(),
        lastSyncMode: 'full',
        lastFullCount: liveCatalogueTotal || firstPage.total || totalSynced,
        deltaUnsupported,
    });

    await emitComplete(context, phase2Result, {
        totalSynced,
        lastPageNum: lastAppliedPage,
        lastFullCount: liveCatalogueTotal || firstPage.total || totalSynced,
        showOverlay,
    });

    return { lastStockSync: maxUpdatedAt, totalSynced };
};

/**
 * Queue a background retry of the paginated full sync after page 0
 * failed. Uses simple exponential backoff (1s, 2s, 4s) capped at
 * three attempts to avoid hammering a flapping server. Runs under
 * the module-level lock so it serialises with any other
 * `getAllStock` call.
 *
 * Intentionally fire-and-forget: failures are logged but never
 * surfaced to the cashier. The retry only attempts page 0; if it
 * succeeds, the next periodic `getAllStock` (from syncService.js)
 * will resume the background pages via its own Phase 2.
 *
 * @param {Object} store default store
 * @param {Object} device device settings
 * @param {Object} user current user
 * @param {Object} syncState current syncState snapshot
 * @param {boolean} deltaUnsupported whether delta is unsupported
 */
const schedulePaginatedRetry = (store, device, user, syncState, deltaUnsupported) => {
    const delays = [1000, 2000, 4000];
    const attempt = (index) => {
        if (index >= delays.length) return;
        setTimeout(() => {
            withStockSyncLock(async () => {
                try {
                    const retryFirst = await fetchStockPage(store, device, user, {
                        page: 0,
                        size: isNativeMobileRuntime()
                            ? STOCK_SYNC_FIRST_PAGE_SIZE_MOBILE
                            : STOCK_SYNC_FIRST_PAGE_SIZE_DESKTOP,
                    });
                    // Page 0 OK — clear catalogue then refill so retry does not
                    // merge onto stale local rows (same contract as main full sync).
                    // stock_dirty is preserved for offline pending sales.
                    await Storage.clearStockItemsOnly();
                    const firstItems = Array.isArray(retryFirst.items) ? retryFirst.items : [];
                    const normalized = firstItems.map(normalizeServerStockItem).filter(Boolean);
                    const apply = await Storage.applyPaginatedStockPage(normalized);
                    // Retry is fire-and-forget background work — no blocking overlay.
                    publishStockProgress(null, {
                        loaded: firstItems.length,
                        total: Number(retryFirst.total) || firstItems.length,
                        page: 0,
                        hasMore: !!retryFirst.hasMore,
                        message: 'Downloading stock…',
                        showOverlay: false,
                        force: true,
                    });
                    await Storage.setStockSyncState({
                        ...syncState,
                        lastStockSync: getMaxUpdatedAt(firstItems, new Date().toISOString()),
                        lastSyncMode: 'partial-page0',
                        lastFullCount: firstItems.length,
                        lastFullSyncOutcome: 'partial-page0',
                        deltaUnsupported,
                    });
                    if (apply?.skipped?.length) {
                        console.log(`[Stock Module] Retry page 0: skipped ${apply.skipped.length} rows due to local dirty conflicts`);
                    }
                } catch (retryErr) {
                    console.warn(`[Stock Module] Paginated retry attempt ${index + 1} failed:`, retryErr?.message || retryErr);
                    attempt(index + 1);
                }
            });
        }, delays[index]);
    };
    attempt(0);
};

const dedupeByPlu = (items) => {
    const unique = new Map();
    const source = Array.isArray(items) ? items : [];
    for (const item of source) {
        if (!item || item.PLU === undefined || item.PLU === null) continue;
        const pluKey = normalizeString(item.PLU);
        if (!unique.has(pluKey)) {
            unique.set(pluKey, item);
        }
    }
    return Array.from(unique.values());
};

const compactStockCacheItem = (item = {}) => ({
    PLU: item.PLU,
    Description: item.Description,
    SellingIncl: item.SellingIncl,
    barcode: item.barcode,
    TaxLabels: item.TaxLabels,
});

export default {
    name: 'stock',
    state: {
        stock: [],
        stockProducts: [],
        stockSetting: [],
        stockSettingMap: {},
        stockItem: {},
        stockCache: new Map(),
        stockLength: 1,
        // Flutter SyncProgress parity for full-catalogue download overlay.
        stockSyncProgress: emptyStockSyncProgress(),
    },
    getters: {
        getStockProducts: (state) => state.stockProducts,
        getAllStockProducts: ({ stockCache }) => [...stockCache.values()],
        getStockItem: (state) => state.stockItem,
        getStockSetting: (state) => state.stockSetting,
        getStockSettingByPlu: (state) => (plu) => {
            const key = normalizeString(plu);
            if (!key) return null;
            return state.stockSettingMap[key] || null;
        },
        getStockLength: (state) => state.stockLength,
        /**
         * Stock full-sync progress for Loader (active / loaded / total / fraction).
         * @param {Object} state
         * @returns {{active: boolean, message: string, loaded: number, total: number, fraction: number|null}}
         */
        getStockSyncProgress: (state) => state.stockSyncProgress || emptyStockSyncProgress(),
        getByPlu: (state) => (plu) => {
            if (plu === undefined || plu === null) return null;
            const normalizedPlu = normalizeString(plu);
            // const item = state.stockCache.get(plu.toLowerCase());
            const item = state.stockCache.get(normalizedPlu);
            if (!item) {
                return state.stock.find(stockItem => normalizeString(stockItem?.PLU) === normalizedPlu);
            }
            return item;
        },
    },

    mutations: {
        /**
         * Replace stock sync progress snapshot used by the full-screen Loader bar.
         * Why a dedicated mutation: EventBus alone is not reactive for Vuex-bound UI;
         * cashiers need loaded/total while Phase 2 runs after page-0 unlock.
         *
         * @param {Object} state
         * @param {Object} payload partial progress fields
         */
        setStockSyncProgress: (state, payload) => {
            const loaded = Math.max(0, Number(payload?.loaded) || 0);
            const total = Math.max(0, Number(payload?.total) || 0);
            const finished = payload?.finished === true;
            let fraction = null;
            if (payload?.fraction != null && Number.isFinite(Number(payload.fraction))) {
                fraction = Math.min(1, Math.max(0, Number(payload.fraction)));
            } else if (total > 0) {
                fraction = Math.min(1, Math.max(0, loaded / total));
            }
            // UI guard: active unfinished progress never stores a full 1.0 bar.
            if (!finished && fraction != null && fraction >= 1) {
                fraction = 0.99;
            }
            state.stockSyncProgress = {
                active: !!payload?.active,
                message: String(payload?.message || ''),
                loaded,
                total,
                fraction,
                finished,
            };
        },
        stockByPLU: async (state, payload) => {
            const isArrayPayload = Array.isArray(payload);
            const hasObjectItems = payload && Array.isArray(payload.items);
            if (isArrayPayload || hasObjectItems) {
                const items = isArrayPayload ? payload : payload.items;
                const shouldPersist = isArrayPayload || payload.persist !== false;
                const source = Array.isArray(items) ? items : [];
                const highVolumeMobile = isNativeMobileRuntime() && source.length > HIGH_VOLUME_STOCK_THRESHOLD;
                if (shouldPersist) {
                    await Storage.stockByPLU(source);
                }

                const fastLineMap = new Map();
                const stockCache = new Map();
                for (let i = 0; i < source.length; i++) {
                    // Plan §A3: normalize at the ingestion boundary so every
                    // item reaching state.stock / state.stockCache has the
                    // canonical `archived` (boolean) and `FastLine` (0|1).
                    const item = normalizeStockFlags(source[i]);
                    const key = normalizeString(item?.PLU);
                    // Plan §C2: surface rows the API returned but the cache
                    // can't key. Helps diagnose a backend that emits junk PLUs.
                    if (!key) {
                        recordRowDrop(item, 'missing-plu');
                        continue;
                    }

                    if (highVolumeMobile) {
                        if (item?.FastLine === 1) {
                            stockCache.set(key, compactStockCacheItem(item));
                        }
                    } else {
                        stockCache.set(key, item);
                    }

                    if (item?.FastLine === 1 && !fastLineMap.has(key)) {
                        fastLineMap.set(key, item);
                    } else if (item?.FastLine === 1 && fastLineMap.has(key)) {
                        // Plan §C3: first-wins dedup means a duplicate PLU
                        // silently shadows the second row. Counting surfaces
                        // a backend data bug that would otherwise be invisible.
                        recordDuplicatePlu();
                    }
                }

                let deduped = Array.from(fastLineMap.values());
                if (highVolumeMobile && deduped.length > MOBILE_FASTLINE_CAP) {
                    // Plan §C4: a 19,999-item drop on a 20k catalogue means
                    // the cashier only sees 1 fastline item. Log the magnitude.
                    recordCap(MOBILE_FASTLINE_CAP, deduped.length - MOBILE_FASTLINE_CAP);
                    deduped = deduped.slice(0, MOBILE_FASTLINE_CAP);
                }
                if (deduped.length <= LARGE_SORT_THRESHOLD) {
                    deduped = deduped.slice().sort(sortByDescription);
                }

                state.stock = deduped;
                state.stockProducts = deduped;
                state.stockCache = stockCache;

            } else {
                await Storage.getFastLine().then(res => {
                    const source = dedupeByPlu(Array.isArray(res) ? res : []);
                    const list = source.length > LARGE_SORT_THRESHOLD
                        ? source
                        : source.slice().sort(sortByDescription);
                    state.stock = list;
                    state.stockProducts = list;
                })
            }
        },
        setStockSetting: async (state, payload) => {
            if (payload) {
                const safePayload = Array.isArray(payload) ? payload : [];
                state.stockSetting = safePayload;
                state.stockSettingMap = safePayload.reduce((acc, setting) => {
                    const key = normalizeString(setting?.plu || setting?.PLU);
                    if (!key) return acc;
                    acc[key] = setting;
                    return acc;
                }, {});
            }
        },
        mergeStockSettings: (state, payload) => {
            const updates = Array.isArray(payload) ? payload : [payload];
            const validUpdates = updates.filter(setting => setting && setting.plu);
            if (validUpdates.length === 0) return;

            const byPlu = new Map(
                (Array.isArray(state.stockSetting) ? state.stockSetting : [])
                    .filter(setting => setting && setting.plu)
                    .map(setting => [normalizeString(setting.plu), setting])
            );

            for (const setting of validUpdates) {
                byPlu.set(normalizeString(setting.plu), { ...byPlu.get(normalizeString(setting.plu)), ...setting });
            }

            state.stockSetting = Array.from(byPlu.values());
            state.stockSettingMap = state.stockSetting.reduce((acc, setting) => {
                const key = normalizeString(setting?.plu || setting?.PLU);
                if (!key) return acc;
                acc[key] = setting;
                return acc;
            }, {});
        },
        setSearch: (state, payload) => {
            const source = dedupeByPlu(Array.isArray(payload) ? payload : []);
            const list = source.length > LARGE_SORT_THRESHOLD
                ? source
                : source.slice().sort(sortByDescription);
            state.stock = list;
            state.stockProducts = list;

            const cache = state.stockCache instanceof Map ? state.stockCache : new Map();
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                const key = normalizeString(item?.PLU);
                if (!key) continue;
                cache.set(key, item);
            }
            if (isNativeMobileRuntime()) {
                while (cache.size > MOBILE_STOCK_CACHE_CAP) {
                    const firstKey = cache.keys().next().value;
                    if (firstKey === undefined) break;
                    cache.delete(firstKey);
                }
            }
            state.stockCache = cache;
        },
        setStockItem: (state, payload) => {
            state.stockItem = payload
        },
        setStockLength: (state, payload) => {
            state.stockLength = payload
        },
        addStockItem: (state, payload) => {
            Storage.getAllStock().then(stock => {
                const pluKey = normalizeString(payload?.PLU);
                if (!pluKey) return;

                const existingAllStock = Array.isArray(stock) ? stock : [];
                const mergedAllStock = existingAllStock
                    .filter(item => normalizeString(item?.PLU) !== pluKey)
                    .concat(payload)
                    .sort(sortByDescription);

                state.stockCache = mergedAllStock.reduce((mapAcc, obj) => {
                    if (!obj || obj.PLU === undefined || obj.PLU === null) return mapAcc;
                    mapAcc.set(normalizeString(obj.PLU), obj);
                    return mapAcc;
                }, new Map());

                let foundInVisible = false;
                const nextVisibleStock = state.stock.map(s => {
                    if (normalizeString(s?.PLU) === pluKey) {
                        foundInVisible = true;
                        return payload;
                    }
                    return s;
                });

                if (!foundInVisible && payload.FastLine === 1 && !payload.archived) {
                    nextVisibleStock.push(payload);
                }

                const deduped = dedupeByPlu(nextVisibleStock).sort(sortByDescription);
                state.stock = deduped;
                state.stockProducts = deduped;
                return Storage.stockByPLU(mergedAllStock);
            });
        },
        mergeServerStockItems: (state, payload) => {
            const updates = Array.isArray(payload) ? payload : [payload];
            const validUpdates = updates.filter(item => item && item.PLU);
            if (validUpdates.length === 0) return;

            if (!(state.stockCache instanceof Map)) {
                state.stockCache = new Map(Object.entries(state.stockCache || {}));
            }

            const currentStock = Array.isArray(state.stock) ? state.stock.slice() : [];
            let stockChanged = false;

            for (const incoming of validUpdates) {
                // Plan §A4: normalize flags so the truthy gate at `!archived`
                // (line below) and the view's `=== false` filter never disagree.
                // Coerces `archived` to boolean and `FastLine` to 0|1 in place.
                const normalizedIncoming = normalizeStockFlags({ ...incoming });
                const hasQty = normalizedIncoming.QTY !== undefined && normalizedIncoming.QTY !== null && normalizedIncoming.QTY !== '';
                if (hasQty) {
                    const qty = Number(normalizedIncoming.QTY);
                    if (Number.isFinite(qty)) {
                        normalizedIncoming.QTY = qty;
                        normalizedIncoming.actualQty = qty;
                    }
                }

                const pluKey = String(normalizedIncoming.PLU).toLowerCase();
                const cached = state.stockCache.get(pluKey) || {};
                const merged = { ...cached, ...normalizedIncoming };
                state.stockCache.set(pluKey, merged);
                state.stockItem = merged;

                const idx = currentStock.findIndex(item => String(item.PLU).toLowerCase() === pluKey);
                if (idx >= 0) {
                    currentStock[idx] = { ...currentStock[idx], ...normalizedIncoming };
                    stockChanged = true;
                } else if (
                    // Phase 2 pages must grow the FastLine grid, not only stockCache.
                    // Without this, new FastLine SKUs stay invisible until getFastLine click.
                    Number(normalizedIncoming.FastLine) === 1
                    && !normalizedIncoming.archived
                ) {
                    currentStock.push(merged);
                    stockChanged = true;
                }
            }

            if (stockChanged) {
                const sorted = currentStock.slice().sort(sortByDescription);
                const deduped = dedupeByPlu(sorted);
                state.stock = deduped;
                state.stockProducts = deduped;
            }
        }
    },
    actions: {
        addStockItem: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            try {
                delete payload.CreatedUser
            } catch (e) {
                console.error(e)
            }
            return axios.post(`${serverApi}stock/${store.StoreDB}`, payload)
                .then(({ data }) => {
                    context.commit('addStockItem', data);
                    context.commit('setStockItem', data);
                    return data;
                })
                .catch((err) => errorHandler.tomcatError(err)).finally(() => context.dispatch('getAllStock'))
        },
        applyServerStockUpdates: async (context, payload) => {
            const updates = Array.isArray(payload) ? payload : [payload];
            const validUpdates = updates
                .filter(item => item && item.PLU)
                .map(item => {
                    const normalized = { ...item };
                    const hasQty = normalized.QTY !== undefined && normalized.QTY !== null && normalized.QTY !== '';
                    if (hasQty) {
                        const qty = Number(normalized.QTY);
                        if (Number.isFinite(qty)) {
                            normalized.QTY = qty;
                            normalized.actualQty = qty;
                        }
                    }
                    return normalized;
                });
            if (validUpdates.length === 0) return [];

            context.commit('mergeServerStockItems', validUpdates);

            try {
                await Storage.upsertStockBatch(validUpdates);
            } catch (err) {
                console.warn('[Stock Module] Failed to persist server stock updates:', err);
            }

            return validUpdates;
        },
        getStockSetting: async (context, plu) => {
            const getByPlu = context.getters.getStockSettingByPlu;
            if (typeof getByPlu === 'function') {
                const cached = getByPlu(plu);
                if (cached) return cached;
            }
            const setting = await Storage.getStockSetting(plu);
            if (setting && (setting.plu || setting.PLU)) {
                context.commit('mergeStockSettings', setting);
            }
            return setting;
        },
        /**
         * Incremental stock sync against `${serverApi}stock/${store.StoreDB}/delta`.
         *
         * Pages through rows whose `updated_at` is newer than `lastStockSync`,
         * normalizes each row via normalizeServerStockItem + normalizeStockSetting,
         * reconciles against local dirty stock (skips rows whose PLU has pending
         * sales — see reconcileServerStockUpdates), and persists in batches.
         *
         * @returns {Promise<{usedDelta: boolean, updatedCount?: number, updatedAt?: string, reason?: string}>}
         *   - { usedDelta: true, updatedCount, updatedAt } on success
         *   - { usedDelta: false, reason: 'missing-prerequisites' } when args are incomplete
         *   - { usedDelta: false, reason: 'delta-endpoint-<status>' } when the server
         *     rejects the delta call (HTTP 400/404/405/501) so the caller can fall
         *     back to a full sync and mark the delta endpoint as unsupported
         *
         * Throws on transport errors so getAllStock can decide to hydrate from
         * the local cache instead.
         */
        /**
         * Pull additional barcodes for offline multi-barcode lookup.
         * GET /stock/{DB}/barcodes → { count, items: [{ plu, barcodes: [] }] }
         * Soft-fails so stock sell path is never blocked.
         */
        syncItemBarcodes: async () => {
            try {
                const store = await Storage.getDefaultStore();
                if (!store?.StoreDB) return { synced: false, reason: 'no-store' };
                const { data } = await axios.get(
                    `${serverApi}stock/${store.StoreDB}/barcodes`,
                    { timeout: 30000 }
                );
                const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                const count = await Storage.replaceAllItemBarcodes(items);
                return { synced: true, count };
            } catch (err) {
                console.warn('[Stock Module] Item barcodes sync failed:', err?.message || err);
                return { synced: false, reason: err?.message || String(err) };
            }
        },
        syncStockDelta: async (context, { store, device, user, lastStockSync }) => {
            // Plan §B2: every delta run starts a fresh integrity report so
            // the EventBus payload at the end reflects exactly this run.
            resetSyncStats('delta');
            if (!store || !store.StoreDB || !lastStockSync) {
                return { usedDelta: false, reason: 'missing-prerequisites', updatedCount: 0 };
            }

            let offset = 0;
            let updatedCount = 0;
            let hasMore = true;
            let maxUpdatedAt = toIsoString(lastStockSync);

            while (hasMore) {
                let response;
                try {
                    response = await axios.get(`${serverApi}stock/${store.StoreDB}/delta`, {
                        params: {
                            updatedAfter: lastStockSync,
                            pc: device?.PC,
                            user: user ? user.Username : undefined,
                            limit: STOCK_DELTA_PAGE_SIZE,
                            offset
                        }
                    });
                } catch (error) {
                    const status = Number(error?.response?.status);
                    if (DELTA_UNSUPPORTED_STATUS.has(status)) {
                        return { usedDelta: false, reason: `delta-endpoint-${status}`, updatedCount: 0 };
                    }
                    throw error;
                }

                const parsed = parseDeltaResponse(response?.data, STOCK_DELTA_PAGE_SIZE);
                const rawItems = Array.isArray(parsed.items) ? parsed.items : [];
                if (rawItems.length === 0) {
                    break;
                }

                const normalizedStock = rawItems.map(normalizeServerStockItem).filter(Boolean);
                const settings = rawItems.map(normalizeStockSetting).filter(Boolean);
                const reconciliation = await Storage.reconcileServerStockUpdates(normalizedStock);
                const stockChunks = chunkArray(reconciliation.applyUpdates, STOCK_SYNC_BATCH_SIZE);

                for (const chunk of stockChunks) {
                    await Storage.upsertStockBatch(chunk);
                    context.commit('mergeServerStockItems', chunk);
                }

                if (reconciliation.clearDirtyPlu.length > 0) {
                    await Storage.clearDirtyStock(reconciliation.clearDirtyPlu);
                }

                // Plan §B2: surface every skipped row on the integrity report
                // so the cashier-ticket question "API has it, POS doesn't"
                // gets an immediate answer from the EventBus payload.
                if (reconciliation.skippedUpdates.length > 0) {
                    for (const skipped of reconciliation.skippedUpdates) {
                        recordSkip(skipped?.plu, skipped?.reason || 'unknown');
                    }
                    // eslint-disable-next-line no-console
                    console.warn(`[Stock Module] Skipped ${reconciliation.skippedUpdates.length} stock delta rows due to local dirty conflicts`);
                }

                if (settings.length > 0) {
                    context.commit('mergeStockSettings', settings);
                }

                updatedCount += reconciliation.applyUpdates.length;
                // Plan §B2: appliedCount mirrors the reconciliation's apply
                // list, not rawItems, so the count tracks what actually landed.
                recordApply(reconciliation.applyUpdates.length);
                maxUpdatedAt = getMaxUpdatedAt(rawItems, maxUpdatedAt);

                hasMore = parsed.hasMore === true || rawItems.length >= STOCK_DELTA_PAGE_SIZE;
                offset += rawItems.length;
            }

            const report = finalizeSyncStats(
                updatedCount === 0 ? 'no-change' : 'complete',
            );
            emitIntegritySummary(report);

            return {
                usedDelta: true,
                updatedCount,
                updatedAt: maxUpdatedAt || toIsoString(new Date().toISOString())
            };
        },
        /**
         * Top-level entry point for fetching stock from OnlineSalesServer.
         *
         * Runs under the module-level `_stockSyncPromise` lock (see
         * `withStockSyncLock`) so concurrent calls (periodic timer
         * from `syncService.js` + manual refresh from `addStockItem`)
         * serialise instead of racing.
         *
         * Flow:
         *   1. Hydrate Vuex state from the local SQLite/IndexedDB cache
         *      so the UI has stock immediately (offline-first).
         *   2. Pre-warm the stockSettingMap (skipped on high-volume
         *      mobile to avoid memory pressure).
         *   3. Throttle background syncs to `options.minIntervalMs`
         *      (15 min from syncService.js) so the periodic timer does
         *      not hammer the server.
         *   4. Verify connectivity via connectivityMonitor. If offline
         *      and the cache has rows, return. If offline and cache is
         *      empty, log and return — no error.
         *   5. If a `lastStockSync` checkpoint exists and the server
         *      has not been marked delta-unsupported, dispatch
         *      `syncStockDelta`. On success, persist the new checkpoint
         *      and return.
 *   6. Otherwise, run the **two-phase paginated full sync**
 *      against `/stock/{DB}/page`:
 *         - Phase 1 (awaits) — fetch page 0 with an adaptive
 *           size (1000 desktop / 500 mobile). On success,
 *           `Storage.clearStockItemsOnly()` wipes local
 *           `stock_items` (keeps `stock_dirty`) so server-deleted
 *           PLUs cannot linger, then page 0 is applied. FastLine
 *           is covered in 1-2 round-trips; loaders/sell path
 *           unblock after page 0 (AB#5546). Page 0 failure never
 *           clears — old cache stays.
 *         - Phase 2 (under same lock) — bounded concurrent page
 *           fetches (4 desktop / 2 mobile) with sequential
 *           apply into the cleared base. Concurrent getAllStock
 *           waits on withStockSyncLock until Phase 2 finishes.
 *      Each page is reconciled against `stock_dirty` via
 *      `Storage.applyPaginatedStockPage` (which wraps
 *      `reconcileServerStockUpdates` + `upsertStockBatch`),
 *      so rows with pending local sales are never overwritten.
 *      `stock:sync-progress` is emitted on the global
 *      `window.EventBus` after each page.
 *   7. On any failure, hydrate from cache; if cache is empty,
 *      surface the error via the global errorHandler.
 *
 * @see doGetAllStock for the actual implementation at module scope
 * @param {Object} options
 * @param {boolean} [options.background=false] when true, honors `minIntervalMs` throttle
 * @param {number}  [options.minIntervalMs=0]   minimum gap between background syncs
 */
        getAllStock: async (context, options = {}) => {
            // Split "UI ready after page 0" from "lock held until Phase 2 done".
            // Callers (loader / sell path) await the returned promise and unblock
            // after page 0. Concurrent getAllStock still serialises via
            // withStockSyncLock until Phase 2 finishes — without nesting a second
            // lock inside doGetAllStock (that pattern deadlocked historically).
            let page0Settled = false;
            let resolvePage0;
            const page0Ready = new Promise((resolve) => {
                resolvePage0 = () => {
                    if (page0Settled) return;
                    page0Settled = true;
                    resolve();
                };
            });

            const lockedWork = withStockSyncLock(() =>
                doGetAllStock(context, {
                    ...options,
                    signalPage0Complete: resolvePage0,
                })
            );

            // Delta / offline / throttle / Phase-1-failure paths never call
            // signalPage0Complete — resolve when the locked work ends so
            // callers never hang on page0Ready.
            lockedWork.then(resolvePage0, resolvePage0);
            lockedWork.catch((err) => {
                console.warn(
                    '[Stock Module] getAllStock lock chain error:',
                    err?.message || err
                );
            });

            return page0Ready;
        },

        searchStock: (context, { query = {}, limit, offset }) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            context.commit('setCurrentCategory', query.category)
            context.commit('setSubCategory', query.subCategory)
            context.commit('setSubCategory2', query.sub2Category)
            return Storage.searchStock(query, limit, offset).then(res => {
                context.commit('setSearch', res)
            }).catch(err => console.error(err))
        },
        searchDescription: (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            context.commit('setCurrentCategory', query.category)
            context.commit('setSubCategory', query.subCategory)
            context.commit('setSubCategory2', query.sub2Category)
            return Storage.searchStockDesc(query).then(res => {
                context.commit('setSearch', res)
            }).catch(err => console.error(err))
        },
        searchPartialDescription: (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            return Storage.searchStockDesc(query, 50).then(res => {
                return res;
            }).catch(err => console.error(err))
        },

        searchDepartment: async (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            query.description = '';
            return Storage.searchStockDesc(query).then((value => value));
        },

        getFastLine: (context, query) => {
            context.commit(`setSearchCategories`, {
                category: ``,
                subCategory: ``,
                sub2Category: ``
            })
            return Storage.getFastLine().then(res => {
                let list = Array.isArray(res) ? res : [];
                if (isNativeMobileRuntime() && list.length > MOBILE_FASTLINE_CAP) {
                    list = list.slice(0, MOBILE_FASTLINE_CAP);
                }
                context.commit('setSearch', list)
            }).catch(err => console.error(err))
        },


    }
}

// Implementation of getAllStock. Lives outside the actions object so the
// withStockSyncLock wrapper above can call it without the recursion.
//
// JSDoc mirrors the getAllStock wrapper above; the parameter list is
// duplicated here to keep TypeScript / IDE completion working when this
// function is reached through a breakpoint stack.
/**
 * @param {Object} context Vuex action context
 * @param {{background?: boolean, minIntervalMs?: number}} [options]
 * @returns {Promise<void>}
 */
const doGetAllStock = async (context, options = {}) => {
    const isBackgroundSync = options?.background === true;
    const skipDelta = options?.skipDelta === true;
    const minIntervalMs = Number(options?.minIntervalMs || 0);
    // Plan §B3: every full-sync run starts a fresh integrity report.
    // Delta runs reset their own (in syncStockDelta) so the two paths
    // cannot leak counters into each other.
    resetSyncStats('full');
    // Self-heal any stale `stock_dirty` rows whose `pending_sales = 0`.
    // The simplified reconciliation (Branch B) clears these rows as it
    // sees them, but a PLU the server hasn't returned since the row went
    // stale would stay stuck. This sweep is the safety net — gated to
    // run at most once per 7 days inside Storage.sweepStaleDirtyStock.
    Storage.sweepStaleDirtyStock().catch(() => void 0);
    let store = await Storage.getDefaultStore();
    let device = await Storage.getDeviceSettings();
    let user = await Storage.getCurrentUser();
    if (!store) return;
    const syncState = await Storage.getStockSyncState();
    const lastStockSync = toIsoString(syncState?.lastStockSync);
    const lastAnySync = toIsoString(syncState?.lastDeltaSync || syncState?.lastFullSync || syncState?.lastStockSync);
    let deltaUnsupported = syncState?.deltaUnsupported === true;
    // Plan §D3: forced full-sync bypass. Mirrors Restaurant `isFullSyncDue`.
    // deltaUnsupported already forces full sync below; this covers the
    // 24h staleness branch as well as the first-run branch.
    const forceFullSync = isFullSyncDue(syncState, Date.now());
    const highVolumeHint = Number(syncState?.lastFullCount || syncState?.lastSyncCount || 0) > HIGH_VOLUME_STOCK_THRESHOLD;
    const highVolumeMobileHint = isNativeMobileRuntime() && highVolumeHint;
    const hydrateFromCache = async () => {
        if (highVolumeMobileHint) {
            let fastLine = await Storage.getFastLine();
            fastLine = Array.isArray(fastLine) ? fastLine : [];
            if (fastLine.length > MOBILE_FASTLINE_CAP) {
                fastLine = fastLine.slice(0, MOBILE_FASTLINE_CAP);
            }
            if (fastLine.length > 0) {
                await context.commit('stockByPLU', { items: fastLine, persist: false });
            }
            return fastLine;
        }

        const cachedStock = await Storage.getAllStock();
        if (cachedStock && cachedStock.length > 0) {
            await context.commit('stockByPLU', { items: cachedStock, persist: false });
        }
        return cachedStock || [];
    };

    // Keep UI hydrated from local cache while network sync attempts run.
    await hydrateFromCache();
    // Pre-warm stock setting map so first add-to-sale click stays fast.
    if (!highVolumeMobileHint) {
        Storage.warmStockSettingsCache().catch(() => void 0);
    }

    if (isBackgroundSync && minIntervalMs > 0 && lastAnySync) {
        const lastMs = new Date(lastAnySync).getTime();
        if (Number.isFinite(lastMs)) {
            const elapsed = Date.now() - lastMs;
            if (elapsed < minIntervalMs) {
                return;
            }
        }
    }

    // Check if online
    let isOnline = connectivityMonitor.getStatus();
    if (isOnline && !isBackgroundSync) {
        try {
            isOnline = await connectivityMonitor.forceCheck();
        } catch (e) {
            isOnline = connectivityMonitor.getStatus();
        }
    }

    if (!isOnline) {
        // Offline: Check if stock exists in storage
        //console.log('[Stock Module] Offline - checking for cached stock');
        const cachedStock = await hydrateFromCache();
        if (cachedStock && cachedStock.length > 0) {
            //console.log('[Stock Module] Using cached stock:', cachedStock.length, 'items');
            // Stock already in storage, no need to fetch
            return;
        } else {
            console.warn('[Stock Module] No cached stock available. Stock fetch skipped while offline.');
            return;
        }
    }

    // Online: Try delta sync first when a checkpoint exists AND a full sync
    // is not due (Plan §D3). forceFullSync covers both the 24h staleness case
    // and the first-run case; deltaUnsupported is already handled by the
    // existing branch. Without this bypass, a delta-missed row stays invisible
    // until logout (the only other full-sync trigger).
    if (!skipDelta && !forceFullSync && lastStockSync && !deltaUnsupported) {
        try {
            const deltaResult = await context.dispatch('syncStockDelta', {
                store,
                device,
                user,
                lastStockSync
            });

            if (deltaResult?.usedDelta) {
                await refreshFastLineGrid(context, {
                    cap: isNativeMobileRuntime(),
                });
                await Storage.setStockSyncState({
                    ...syncState,
                    lastStockSync: deltaResult.updatedAt || lastStockSync,
                    lastDeltaSync: new Date().toISOString(),
                    lastSyncMode: 'delta',
                    lastDeltaCount: deltaResult.updatedCount || 0,
                });
                await context.dispatch('syncItemBarcodes');
                return;
            }

            if (String(deltaResult?.reason || '').startsWith('delta-endpoint-')) {
                deltaUnsupported = true;
                await Storage.setStockSyncState({
                    ...syncState,
                    deltaUnsupported: true,
                    deltaUnsupportedReason: deltaResult.reason,
                    deltaUnsupportedAt: new Date().toISOString(),
                });
            }
        } catch (deltaError) {
            console.warn('[Stock Module] Delta sync failed, falling back to paginated full sync:', deltaError);
        }
    }

    // Paginated full sync via /stock/{DB}/page (Phase 1 awaits,
    // Phase 2 fires in the background). Replaces the legacy
    // streaming-array endpoint so 10k+ SKU stores stop pulling the
    // entire catalogue in one response.
    const firstPageSize = isNativeMobileRuntime()
        ? STOCK_SYNC_FIRST_PAGE_SIZE_MOBILE
        : STOCK_SYNC_FIRST_PAGE_SIZE_DESKTOP;

    // Blocking bar for interactive full sync only — periodic SyncService
    // background runs must not cover the POS (Flutter header Sync is explicit).
    const showOverlay = !isBackgroundSync;
    if (showOverlay) {
        publishStockProgress(context, {
            loaded: 0,
            total: 0,
            message: 'Downloading stock…',
            active: true,
            force: true,
            showOverlay: true,
        });
    }

    // PHASE 1 — sync first page so the FastLine grid is fresh
    // before the caller continues.
    let firstPage;
    try {
        firstPage = await fetchStockPage(store, device, user, { page: 0, size: firstPageSize });
    } catch (err) {
        // Phase 1 failure: keep the cashier selling from cache,
        // queue a background retry, and do NOT throw to the caller.
        if (showOverlay) {
            const report = finalizeSyncStats('failed', { reason: 'page0-failed' });
            emitIntegritySummary(report);
            clearStockProgress(context, 'partial', { reason: 'page0-failed', integrity: report });
        }
        const status = Number(err?.response?.status);
        const message = err?.message || String(err);
        const statusMsg = Number.isFinite(status) ? ` (status ${status})` : '';
        console.warn(`[Stock Module] Paginated full sync: page 0 failed${statusMsg}: ${message}`);

        const isNetworkError = !err?.response || err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK';
        if (isNetworkError) {
            try { connectivityMonitor.setOnline(false); } catch (e) { /* ignore */ }
        }

        const cachedStock = await hydrateFromCache();
        if (!cachedStock || cachedStock.length === 0) {
            return errorHandler.tomcatError(err);
        }
        // Queue a background retry with simple exponential backoff.
        schedulePaginatedRetry(store, device, user, syncState, deltaUnsupported);
        return;
    }

    try {
        // Full-sync replace: page 0 is already in hand, so wipe local
        // stock_items before refill. Server-deleted PLUs cannot linger under
        // page-by-page upsert. stock_dirty is left alone (offline sales).
        // Never clear before this point — page 0 failure keeps the old cache.
        await Storage.clearStockItemsOnly();

        const firstItems = Array.isArray(firstPage.items) ? firstPage.items : [];
        const normalizedFirstStock = firstItems.map(normalizeServerStockItem).filter(Boolean);
        const settingsFirstPage = firstItems.map(normalizeStockSetting).filter(Boolean);

        // Plan §B3: capture serverTotal from the envelope so the integrity
        // report can detect "API returned N but local store has M" mismatches.
        const page0Total = Number(firstPage.total);
        if (Number.isFinite(page0Total) && page0Total >= 0) {
            getSyncStats().serverTotal = page0Total;
        }

        // Reconcile against dirty stock and persist the surviving
        // items. Keeps `stock_dirty` rows with pending sales intact.
        const firstApply = await Storage.applyPaginatedStockPage(normalizedFirstStock);
        if (firstApply?.applied?.length) {
            context.commit('mergeServerStockItems', firstApply.applied);
        }
        // Plan §B3: page-0 contributes to applied + skipped counters exactly
        // like any Phase-2 page. Without this, the integrity report would be
        // short by page-0's count.
        recordApply(firstApply?.applied?.length || 0);
        if (Array.isArray(firstApply?.skipped)) {
            for (const skipped of firstApply.skipped) {
                recordSkip(skipped?.plu, skipped?.reason || 'unknown');
            }
        }
        if (settingsFirstPage.length > 0) {
            context.commit('mergeStockSettings', settingsFirstPage);
        }

        // Page-0 FastLine grid from SQL (same helper as Phase 2 complete).
        await refreshFastLineGrid(context, {
            cap: !!(isNativeMobileRuntime() && highVolumeMobileHint),
        });

        const page0Loaded = firstItems.length;
        const page0HasMore = page0Loaded >= firstPageSize
            || (!!firstPage.hasMore && Number.isFinite(page0Total) && page0Total > page0Loaded);
        publishStockProgress(context, {
            loaded: page0Loaded,
            total: Number.isFinite(page0Total) && page0Total > 0
                ? Math.max(page0Total, page0Loaded)
                : page0Loaded,
            page: 0,
            hasMore: page0HasMore,
            message: 'Downloading stock…',
            force: true,
            showOverlay,
            finished: false,
        });

        // AB#5546 — Unlock till after page 0: signal callers (getAllStock's
        // page0Ready) so loaders/sell path continue while Phase 2 runs.
        // Phase 2 is still awaited below inside this same doGetAllStock
        // invocation, which is the body of withStockSyncLock — so the module
        // lock stays held until Phase 2 completes and a second getAllStock
        // cannot race merges. We intentionally do NOT nest another
        // withStockSyncLock here (that deadlocked when the inner lock queued
        // behind the outer lock that doGetAllStock itself held).
        // Overlay stays active through Phase 2 (Flutter StoreSyncLoadingView parity).
        if (typeof options.signalPage0Complete === 'function') {
            try {
                options.signalPage0Complete();
            } catch (_) {
                // Signal failures must never break the sync.
            }
        }

        // PHASE 2 — bounded parallel page fetches + sequential apply.
        // Still awaited under the outer lock (see comment above). Per-page
        // timeout in fetchStockPage (30s) bounds worst case per request.
        // No overall timeout — large catalogues may take several minutes.
        // Stop signal is items.length < pageSize (not hasMore) for servers
        // that return hasMore=false incorrectly.
        const backgroundSync = runBackgroundStockPages({
            context,
            store,
            device,
            user,
            firstPage,
            syncState,
            deltaUnsupported,
            isNativeMobileRuntime: isNativeMobileRuntime(),
            highVolumeMobileHint,
            showOverlay,
        });
        let phase2Outcome = 'complete';
        await backgroundSync.catch((bgErr) => {
            console.warn('[Stock Module] Background page sync failed:', bgErr);
            phase2Outcome = 'partial';
        });
        await context.dispatch('syncItemBarcodes');

        // Plan §B3 + §B5: finalize the integrity report after Phase 2 settles
        // and dispatch a single console.warn if the local count diverges from
        // the server's reported total. Warn-only — never blocking — because
        // the legitimate reasons for a mismatch include server-side deletions
        // captured locally between the page-0 fetch and the last page apply.
        const localCount = await Storage.countAllStock().catch(() => null);
        // Verify-gate (was warn-only until the page-1 skip regression taught us
        // it's not enough). When the page-0 envelope carries a usable total
        // (> 0), the local row count MUST reach it. `localCount < serverTotal`
        // means we lost a window (or more) — accept the run as `incomplete` so
        // the staleness clock stays put and isFullSyncDue forces another full
        // wipe+refill. `localCount > serverTotal` is left as warn-only because
        // it has a legitimate cause (server-side deletions captured mid-sync,
        // noted in the existing comment) and gating would risk infinite
        // re-runs on stores where the delta endpoint actually works.
        const serverTotalRaw = Number(getSyncStats().serverTotal);
        const serverTotalUsable = Number.isFinite(serverTotalRaw) && serverTotalRaw > 0
            ? serverTotalRaw
            : null;
        if (Number.isFinite(localCount) && serverTotalUsable !== null && localCount < serverTotalUsable) {
            const shortfall = serverTotalUsable - localCount;
            console.warn(
                '[Stock Integrity] serverTotal != localCount — accepting run as incomplete to force re-sync',
                { serverTotal: serverTotalUsable, localCount, shortfall, appliedCount: getSyncStats().appliedCount },
            );
            phase2Outcome = 'incomplete';
        } else if (Number.isFinite(getSyncStats().serverTotal) && Number.isFinite(localCount)) {
            const serverTotal = Number(getSyncStats().serverTotal);
            if (serverTotal !== localCount) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[Stock Integrity] serverTotal != localCount (local extra or unknown server total — warn only)',
                    { serverTotal, localCount, appliedCount: getSyncStats().appliedCount },
                );
            }
        }

        // Plan §D4 (corrected): persist `lastFullSync` ONLY on a verified
        // `complete` outcome. Before this gate, `lastFullSync` was always
        // written — including when page-1's persist had silently been
        // swallowed (see the in-place retry above) — which let partial runs
        // reset the 24h staleness clock and made any `localCount < serverTotal`
        // hole invisible to every subsequent cycle. We keep the clock
        // honest now: incomplete = no clock advance = next periodic sync
        // still sees `isFullSyncDue` as true (deltaUnsupported already forces
        // that on this device; on delta-capable devices the staleness clock
        // is the only re-sync signal).
        const persistState = {
            ...syncState,
            lastFullCount: Number.isFinite(localCount) ? localCount : (syncState?.lastFullCount || 0),
            lastFullSyncOutcome: phase2Outcome,
        };
        if (phase2Outcome === 'complete') {
            persistState.lastFullSync = new Date().toISOString();
        }
        await Storage.setStockSyncState(persistState);

        const report = finalizeSyncStats(phase2Outcome);
        emitIntegritySummary(report);
    } catch (err) {
        console.error('[Stock Module] Failed to process paginated stock page 0:', err);
        if (showOverlay) {
            const report = finalizeSyncStats('failed', { reason: 'page0-process-failed' });
            emitIntegritySummary(report);
            clearStockProgress(context, 'partial', { reason: 'page0-process-failed', integrity: report });
        }
        const cachedStock = await hydrateFromCache();
        if (cachedStock && cachedStock.length > 0) {
            return;
        }
        throw err;
    }
};
